import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { StoreInfoCells, StoreInfoHead } from "./StoreInfoCells";
import { ExportButton, TotalsRow } from "./ReportTotals";
import { HISTORY_REPORTS, STORE_COLS } from "./ReportHistory";
import { Activity, AlertTriangle, CheckCircle2, Loader2, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/verizon-sales`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const Uploader = ({ onDone }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/admin/pulse/upload`, fd, auth());
      toast.success(`Loaded ${r.data.rows} stores (${r.data.matched} matched)`);
      if (r.data.unmatched?.length) toast.warning(`Not matched: ${r.data.unmatched.join(", ")}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };
  return (
    <label className="inline-flex" data-testid="pulse-upload">
      <input type="file" accept=".csv" className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid="pulse-upload-input" />
      <span className="inline-flex items-center gap-2 h-9 px-3 rounded-md bg-teal text-white text-sm font-medium cursor-pointer hover:bg-teal/90 transition-colors">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Upload Pulse Check CSV
      </span>
    </label>
  );
};

const Cell = ({ v, bad, pct }) => (
  <td className={`py-2 px-3 text-right ${bad ? "text-red-600 font-semibold bg-red-50" : ""}`}>{v == null ? "—" : pct ? `${v}%` : v}</td>
);

const Row = ({ r, admin }) => (
  <tr className={`border-b border-slate-100 ${r.issues.length ? "bg-amber-50/40" : ""}`} data-testid={`pulse-row-${r.door_tsp || r.store_id}`}>
    {admin && <StoreInfoCells r={r} />}
    <td className="py-2 px-3 font-medium text-navy">{r.store_id || "—"}</td>
    <td className="py-2 px-3 text-slate-600">{r.door_tsp || "—"}</td>
    <Cell v={r.ta_training} pct bad={r.ta_training != null && r.ta_training < 100} />
    <Cell v={r.training_pct} pct bad={r.training_pct != null && r.training_pct < 100} />
    <Cell v={r.merchlink_pending} bad={r.merchlink_pending > 0} />
    <Cell v={r.b2b_remaining} bad={r.b2b_remaining > 0} />
    <Cell v={r.ipad_offline} bad={r.ipad_offline > 0} />
    <td className="py-2 px-3 vz-wide" title={r.issues.join(", ")} data-testid={`pulse-status-${r.door_tsp || r.store_id}`}>
      {r.issues.length
        ? <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700"><AlertTriangle className="w-3.5 h-3.5" />{r.issues.join(", ")}</span>
        : <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600"><CheckCircle2 className="w-3.5 h-3.5" />All good</span>}
    </td>
  </tr>
);

const HEAD = ["Store ID", "TSP ID", "TA Training", "Training %", "Merchlink", "B2B", "iPad Offline", "Needs attention"];

export const PulseCheckReport = ({ mode = "admin", canUpload = false }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => axios.get(`${API}/${mode}/pulse`, auth()).then((r) => setD(r.data)).catch(() => setD({ rows: [] })), [mode]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>;
  if (mode === "dealer" && !d.rows.length) return null;
  const flagged = d.rows.filter((r) => r.issues.length).length;
  return (
    <Card className="border-slate-200" data-testid="pulse-report">
      <CardContent className="p-4 lg:p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-heading font-semibold text-navy text-lg flex items-center gap-2"><Activity className="w-5 h-5 text-teal" />Pulse Check</h3>
            <p className="text-sm text-slate-500" data-testid="pulse-summary">
              {d.uploaded_at ? `Updated ${new Date(d.uploaded_at).toLocaleString()} · ${flagged} of ${d.rows.length} stores need attention` : "No Pulse Check report uploaded yet."}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">{canUpload && <Uploader onDone={load} />}<ExportButton title="Pulse Check" cols={[...STORE_COLS(mode === "admin"), ...HISTORY_REPORTS.pulse.cols, { label: "Needs attention", get: (r) => r.issues.join(", ") || "All good" }]} rows={d.rows} testId="pulse-export-btn" /></div>
        </div>
        {d.rows.length > 0 && (
          <div className="overflow-hidden">
            <table className="vz-compact w-full">
              <thead><tr className="border-b border-slate-200 text-left text-slate-500">
                {mode === "admin" && <StoreInfoHead />}
                {HEAD.map((h, i) => <th key={h} className={`py-2 px-3 font-medium ${i > 1 && i < 7 ? "text-right" : ""}`}>{h}</th>)}
              </tr></thead>
              <tbody>{d.rows.map((r, i) => <Row key={`${r.door_tsp}-${i}`} r={r} admin={mode === "admin"} />)}</tbody>
              <TotalsRow rows={d.rows} lead={mode === "admin" ? 4 : 2} label={`Total (${d.rows.length})`} cols={HISTORY_REPORTS.pulse.cols} />
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
